import type { Logger } from "@/utils/logger";
import { createApp, reactive } from "vue";
import type { App as VueApp } from "vue";
import {
  ToastNotificationCenter,
  type ToastNotificationCenterState,
  type ToastNotificationItem,
} from "./vue/toast-notification-center";
import type { SDKState } from "@/state/sdk-state";

// Toast manager for handling multiple toasts
export class ToastNotificationManager {
  #logger: Logger;
  #state: SDKState;
  #container: HTMLElement | null = null;
  #notificationCenter: VueApp | null = null;
  #toasts: ToastNotificationItem[];
  #toastCenterState: ToastNotificationCenterState;

  #removeTimer: number | null = null;

  constructor(logger: Logger, state: SDKState) {
    this.#logger = logger.extend(this.constructor.name);
    this.#state = state;
    this.#toasts = reactive([]);
    this.#toastCenterState = reactive({
      hasUser: this.#state.authState.hasUser,
      isAuthenticated: this.#state.authState.isAuthenticated,
      isAuthenticating: this.#state.authState.isAuthenticating.value,
      showToasts: false,
    });

    this.#state.authState.isAuthenticating.subscribe((v, prev) => {
      this.#toastCenterState.isAuthenticating = v;

      if (v && !prev) {
        this.showToastsCenter();
      }
    });
    this.#state.authState.value.subject.subscribe(
      (authState, previousAuthState) => {
        this.#toastCenterState.hasUser = (authState?.user ?? null) !== null;
        this.#toastCenterState.isAuthenticated = !!authState?.authToken;

        this.#logger.debug("Auth state changed", {
          authState,
          previousAuthState,
        });

        if (!previousAuthState?.user && !!authState?.user) {
          this.showToastsCenter();
        }
      },
    );
  }

  init(): void {
    const container = document.createElement("div");
    container.id = "fanfest-toast-manager";
    document.body.appendChild(container);

    this.#container = container;
    this.#notificationCenter = createApp(ToastNotificationCenter, {
      state: this.#toastCenterState,
      toasts: this.#toasts,
      cleanToasts: this.clearAll,
      goLogin: () => {
        this.#state.options.hostLoginFn?.();
      },
      goRewards: () => {
        this.#state.options.hostRewardsFn?.();
      },
    });
    this.#notificationCenter.mount(container);
  }

  detach(): void {
    if (this.#notificationCenter) {
      this.#notificationCenter.unmount();
      this.#notificationCenter = null;
    }

    if (this.#container) {
      this.#container.remove();
      this.#container = null;
    }

    this.#toasts.splice(0, this.#toasts.length);
    this.#toastCenterState.hasUser = false;
    this.hideToastsCenter();
  }

  showToastsCenter(duration = 5000): void {
    this.#toastCenterState.showToasts = true;

    if (this.#removeTimer) {
      clearTimeout(this.#removeTimer);
    }

    this.#removeTimer = setTimeout(() => {
      this.hideToastsCenter();
    }, duration);
  }

  hideToastsCenter(): void {
    this.#toastCenterState.showToasts = false;

    if (this.#removeTimer) {
      clearTimeout(this.#removeTimer);
      this.#removeTimer = null;
    }
  }

  show(toast: Omit<ToastNotificationItem, "id">): string {
    if (!this.#notificationCenter) {
      this.#logger.debug(
        "ToastNotificationManager: show() called while uninitialized — suppressing toast",
        { toast },
      );
      return "";
    }

    this.#toastCenterState.showToasts = true;

    const id = `toast-${Date.now()}`;
    this.#toasts.push({
      id,
      ...toast,
    });

    this.showToastsCenter(toast.duration ?? 5000);
    return id;
  }

  remove = (id: string): void => {
    if (!this.#notificationCenter) {
      throw new Error(
        this.#logger.prefixMessage(
          "ToastNotificationManager: Container not initialized",
        ),
      );
    }

    const i = this.#toasts.findIndex((toast) => toast.id === id);
    this.#toasts.splice(i, 1);
    this.#toastCenterState.showToasts = this.#toasts.length > 0;
  };

  clearAll = (): void => {
    if (!this.#notificationCenter) {
      throw new Error(
        this.#logger.prefixMessage(
          "ToastNotificationManager: Container not initialized",
        ),
      );
    }

    this.#toasts.splice(0, this.#toasts.length);
    this.#toastCenterState.showToasts = false;
  };
}
